import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext'; // 1. Import your auth hook

export function usePatientRealtime(mrn, isBatchProcessing, onMessage) {
    const { user } = useAuth(); // 2. Extract the authenticated user object
    const channelIdRef = useRef(`chat-patient-updates-${mrn}-${crypto.randomUUID()}`);

    // Keep track of batch processing state cleanly without re-triggering the effect
    const isBatchProcessingRef = useRef(isBatchProcessing);
    useEffect(() => {
        isBatchProcessingRef.current = isBatchProcessing;
    }, [isBatchProcessing]);

    // Keep track of callback cleanly
    const onMessageRef = useRef(onMessage);
    useEffect(() => {
        onMessageRef.current = onMessage;
    }, [onMessage]);

    useEffect(() => {
        // 3. Guard clause: Ensure we have an MRN AND a valid patient client instance
        if (!mrn || !user?.client) return;
        console.log('Checking dependencies for patient realtime initialization...');

        // Use the patient-scoped client from auth state
        const patientClient = user.client;
        
        const channel = patientClient
            .channel(channelIdRef.current)
            .on(
                'postgres_changes',
                {
                    event: '*', 
                    schema: 'public',
                    table: 'patient_conversations',
                },
                async (payload) => {
                    
                    if (!isBatchProcessingRef.current) return;

                    if (payload.new.status_transcribe_bulk === 'completed' && payload.new.transcribe_bulk) {
                        onMessageRef.current(payload.new);
                        // 4. Pass the custom patient client to the update function
                        wipeTranscribeBulk(patientClient, payload.new.id); 
                    }
                }
            )
            .subscribe((status) => {
                console.log(`🔌 Patient Realtime status for channel ${channelIdRef.current}:`, status);
            });

        return () => {
            // 5. Clean up the channel on the correct client instance
            patientClient.removeChannel(channel);
        };
    }, [mrn, user?.client]); // 6. Add user?.client as a dependency so it reconnects if the client shifts
}

// Helper function updated to receive and use the authorized client instance
async function wipeTranscribeBulk(clientInstance, rowId) {
    try {
        const { error } = await clientInstance
            .from('patient_conversations')
            .update({
                transcribe_bulk: null,
                status_transcribe_bulk: null
            })
            .eq('id', rowId);

        if (error) throw error;
        console.log(`[UI Cleanup] Wiped temporary bulk transcript for row ${rowId}`);
    } catch (err) {
        console.error('[UI Cleanup] Failed to clear temporary transcript columns:', err);
    }
}